import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useFireSessionContext } from '@chilipiper/fire-session'
import {
  CreateTaskRequest,
  TaskConfig,
  TaskPublic,
  UpdateTaskRequest,
} from '@chilipiper/api-client-kosmotime'
import { externalCalendarQueryKeys as queryKeyFactoryExternalCalendar } from '@chilipiper/integrations'
import { useApiClient } from '@/api/apiClientContext'

const queryKey = 'kosmotime'
const queryKeyFactory = {
  tasks: (tenantId: string) => [queryKey, 'tasks', tenantId],
  taskById: (tenantId: string, taskId: string) => [queryKey, 'task-by-id', tenantId, taskId],
  session: (tenantId: string) => [queryKey, 'current-session', tenantId],
  sessions: (tenantId: string) => [queryKey, 'sessions', tenantId],
  defaultConfig: (tenantId: string) => [queryKey, 'default-config', tenantId],
  sessionByTaskId: (tenantId: string, taskId: string) => [
    queryKey,
    'session-by-task-id',
    tenantId,
    taskId,
  ],
}

const TIME_TO_PING_EXTERNAL_EVENTS = 500

export const useTasks = () => {
  const { tenantId } = useFireSessionContext()
  const { kosmotime } = useApiClient()

  return useQuery({
    queryKey: queryKeyFactory.tasks(tenantId),
    queryFn: () =>
      kosmotime
        .listTasks(tenantId, {
          pageSize: 100_000,
        })
        .then(paginatedResult => paginatedResult.results),
  })
}

export const useTaskById = (taskId: string) => {
  const { tenantId } = useFireSessionContext()
  const { kosmotime } = useApiClient()

  return useQuery({
    queryKey: queryKeyFactory.taskById(tenantId, taskId),
    queryFn: () => kosmotime.getTaskById(tenantId, taskId),
    enabled: !!taskId,
  })
}

export const useSessionsByTaskId = (taskId: string) => {
  const { tenantId } = useFireSessionContext()
  const { kosmotime } = useApiClient()

  return useQuery({
    queryKey: queryKeyFactory.sessionByTaskId(tenantId, taskId),
    queryFn: () =>
      kosmotime.listSession(tenantId, { taskId }).then(paginatedResult => paginatedResult.results),
    enabled: !!taskId,
  })
}

export const useDeleteTask = (range: { end: string; start: string; userIds: string[] }) => {
  const { tenantId } = useFireSessionContext()
  const { kosmotime } = useApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (taskId: string) => {
      return kosmotime.deleteTask(tenantId, taskId)
    },
    onMutate: removedTaskId => {
      const previousTasks = queryClient.getQueryData<TaskPublic[]>(queryKeyFactory.tasks(tenantId))
      queryClient.setQueryData<TaskPublic[]>(queryKeyFactory.tasks(tenantId), previousTasks =>
        previousTasks?.filter(task => task.id !== removedTaskId)
      )

      return () => {
        queryClient.setQueryData(queryKeyFactory.tasks(tenantId), previousTasks)
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeyFactory.session(tenantId) })
      await queryClient.invalidateQueries({ queryKey: queryKeyFactory.tasks(tenantId) })
      await queryClient.invalidateQueries({ queryKey: queryKeyFactory.sessions(tenantId) })

      // workaround to handle the situation where we should wait for related events of sessions/tasks to be removed as well
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: queryKeyFactoryExternalCalendar.listEvents(range),
        })
      }, TIME_TO_PING_EXTERNAL_EVENTS)
    },
  })
}

export const useUpdateTask = () => {
  const { tenantId } = useFireSessionContext()
  const { kosmotime } = useApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      updatedTaskId,
      updatedData,
    }: {
      updatedTaskId: string
      updatedData: UpdateTaskRequest
    }) => {
      return kosmotime.updateTask(tenantId, updatedTaskId, updatedData)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeyFactory.tasks(tenantId) })
      await queryClient.invalidateQueries({ queryKey: queryKeyFactory.sessions(tenantId) })
    },
    onMutate: ({ updatedTaskId, updatedData }) => {
      const previousTasks = queryClient.getQueryData<TaskPublic[]>(queryKeyFactory.tasks(tenantId))
      const updatedTasks = previousTasks?.map(task => {
        if (task.id === updatedTaskId) {
          if (
            updatedData.type === 'Complete' &&
            (task.state.type === 'Scheduled' || task.state.type === 'Backlog')
          ) {
            const updatedTask: TaskPublic = {
              ...task,
              state: {
                type: 'Completed',
                at: new Date().toISOString(),
                previous: { ...task.state },
              },
            }

            return updatedTask
          }

          if (
            updatedData.type === 'Uncomplete' &&
            task.state.type === 'Completed' &&
            (task.state.previous.type === 'Scheduled' || task.state.previous.type === 'Backlog')
          ) {
            const updatedTask: TaskPublic = {
              ...task,
              state: {
                ...task.state.previous,
              },
            }

            return updatedTask
          }

          if (updatedData.type === 'Schedule' && task.state.type === 'Scheduled') {
            const updatedTask: TaskPublic = {
              ...task,
              state: {
                type: 'Scheduled',
                when: {
                  ...updatedData.when,
                },
              },
            }

            return updatedTask
          }

          return { ...task, ...updatedData }
        }
        return task
      })
      queryClient.setQueryData<TaskPublic[]>(queryKeyFactory.tasks(tenantId), updatedTasks)
      return () => {
        queryClient.setQueryData(queryKeyFactory.tasks(tenantId), previousTasks)
      }
    },
  })
}

export const useCreateTask = () => {
  const { tenantId } = useFireSessionContext()
  const { kosmotime } = useApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: CreateTaskRequest) => {
      return kosmotime.createTask(tenantId, payload)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeyFactory.tasks(tenantId) }),
  })
}

export const useCurrentSession = () => {
  const { tenantId } = useFireSessionContext()
  const { kosmotime } = useApiClient()

  return useQuery({
    queryKey: queryKeyFactory.session(tenantId),
    queryFn: () => kosmotime.getCurrentSession(tenantId),
  })
}

export const useCreateNewSession = () => {
  const { tenantId } = useFireSessionContext()
  const { kosmotime } = useApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (taskId: string) => {
      return kosmotime.createNewSession(tenantId, { taskId })
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeyFactory.session(tenantId) }),
  })
}

export const useHaltSession = () => {
  const { tenantId } = useFireSessionContext()
  const { kosmotime } = useApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => {
      return kosmotime.haltSession(tenantId)
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeyFactory.session(tenantId) })
      await queryClient.invalidateQueries({ queryKey: queryKeyFactory.sessions(tenantId) })
    },
  })
}

export const useCompleteSession = (sessionTaskId?: string) => {
  const { tenantId } = useFireSessionContext()
  const { kosmotime } = useApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => {
      return kosmotime.completeSession(tenantId)
    },
    onMutate: () => {
      const previousTasks = queryClient.getQueryData<TaskPublic[]>(queryKeyFactory.tasks(tenantId))

      const updatedTasks = previousTasks?.map(task => {
        if (
          task.id === sessionTaskId &&
          (task.state.type === 'Scheduled' || task.state.type === 'Backlog')
        ) {
          const updatedTask: TaskPublic = {
            ...task,
            state: {
              type: 'Completed',
              at: new Date().toISOString(),
              previous: { ...task.state },
            },
          }

          return updatedTask
        }

        return task
      })

      queryClient.setQueryData<TaskPublic[]>(queryKeyFactory.tasks(tenantId), updatedTasks)
      return () => {
        queryClient.setQueryData(queryKeyFactory.tasks(tenantId), previousTasks)
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeyFactory.session(tenantId) })
      await queryClient.invalidateQueries({ queryKey: queryKeyFactory.sessions(tenantId) })
    },
  })
}

export const useDefaultConfig = () => {
  const { tenantId } = useFireSessionContext()
  const { kosmotime } = useApiClient()

  return useQuery({
    queryKey: queryKeyFactory.defaultConfig(tenantId),
    queryFn: () => kosmotime.getDefaultConfig(tenantId),
  })
}

export const useUpdateDefaultConfig = () => {
  const { tenantId } = useFireSessionContext()
  const { kosmotime } = useApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: TaskConfig) => {
      return kosmotime.setupDefaultTaskConfig(tenantId, data)
    },
    onMutate: (data: TaskConfig) => {
      const previousDefaultConfig = queryClient.getQueryData<TaskConfig>(
        queryKeyFactory.defaultConfig(tenantId)
      )

      const updatedDefaultConfig = {
        ...previousDefaultConfig,
        ...data,
      }

      queryClient.setQueryData<TaskConfig>(
        queryKeyFactory.defaultConfig(tenantId),
        updatedDefaultConfig
      )

      return () => {
        queryClient.setQueryData(queryKeyFactory.defaultConfig(tenantId), previousDefaultConfig)
      }
    },
  })
}
